import { useState } from "react";
import { Check, Search } from "lucide-react";

const chipBase = "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13px] font-medium cursor-pointer transition-all duration-150 select-none";
const chipOn = "border-brand bg-brand/15 text-ink";
const chipOff = "border-hairline text-ink-soft hover:border-brand/40 hover:text-ink";

export const Chip = ({ active, disabled, onClick, children, testId }) => (
  <button
    type="button" data-testid={testId} disabled={disabled}
    onClick={onClick}
    className={`${chipBase} ${active ? chipOn : chipOff} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
  >
    {active && <Check className="h-3 w-3" />}
    {children}
  </button>
);

// One question's multi-select chip cloud. `max` (blueprint's "select any N") greys out the
// remaining unselected chips once the cap is hit — same UX as the prototype's Project Types
// step. `searchable` (blueprint's Gear/Software "optional, quick-tick" note) adds a filter box
// above the cloud for long option lists instead of paging.
export const MultiSelectField = ({ label, options, value, onChange, max, searchable, optional, testId }) => {
  const [query, setQuery] = useState("");
  const shown = searchable && query ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase())) : options;
  const capped = max && value.length >= max;
  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else if (!capped) onChange([...value, opt]);
  };
  return (
    <div data-testid={testId}>
      <label className="text-[11px] font-medium uppercase tracking-wider text-faint flex items-center gap-1.5">
        {label} {optional && <span className="text-faint/70 normal-case">(optional)</span>}
        {max && <span className="text-faint/70 normal-case">(up to {max})</span>}
      </label>
      {searchable && options.length > 8 && (
        <div className="relative mt-2 mb-2 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-faint" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" data-testid={`${testId}-search`}
            className="w-full rounded-md border border-hairline bg-paper pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:border-brand/50" />
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {shown.length === 0 && <div className="text-xs text-faint py-1">No matches.</div>}
        {shown.map((opt) => (
          <Chip key={opt} active={value.includes(opt)} disabled={!value.includes(opt) && capped}
            onClick={() => toggle(opt)} testId={`${testId}-opt-${opt}`}>
            {opt}
          </Chip>
        ))}
      </div>
    </div>
  );
};

export const SingleSelectField = ({ label, options, value, onChange, testId }) => (
  <div data-testid={testId}>
    <label className="text-[11px] font-medium uppercase tracking-wider text-faint">{label}</label>
    <div className="mt-2 flex flex-wrap gap-2">
      {options.map((opt) => (
        <Chip key={opt} active={value === opt} onClick={() => onChange(opt)} testId={`${testId}-opt-${opt}`}>{opt}</Chip>
      ))}
    </div>
  </div>
);
